import { IdentifierKeyMap, TIdentifierValue } from "@/constants/Test";
import MODULE_REGISTRY from "@/modules";
import { updateRouterConfig } from "@redux/slice/router";
import { useAppDispatch, useAppSelector } from "@redux/store";
import { ErrorBoundaryElement } from "@routers/ErrorBoundary";
import { fetch_RouterConfig, fetch_ShareableLink } from "@services/index";
import { IConfig } from "@type/router";
import MainLayout from "@views/index";
import { Context, useEffect, useState } from "react";
import { createBrowserRouter, LoaderFunctionArgs, Navigate, RouteObject, RouterProvider, RouterProviderProps } from "react-router";

async function infoLoader(args: LoaderFunctionArgs<Context<any>>) {
  const params = new URLSearchParams(args.request.url.split("?")[1]);
  const shareId = params.get("ID") ?? params.get("id") ?? params.get("shareId");
  if (shareId) {
    const response = await fetch_ShareableLink(shareId);
    const { requestParameter, ...meta } = response.data;

    // Parse the requestParameter JSON string into a usable object
    let parsed: Record<string, any> = {};
    try {
      parsed = requestParameter ? JSON.parse(requestParameter) : {};
    } catch {
      console.warn("Failed to parse shareable link requestParameter:", requestParameter);
    }

    // Build identifier from parsed selections (if present) or fall back to flat identifier fields
    let identifier: Record<string, string> = {};
    if (parsed.selections && Array.isArray(parsed.selections) && parsed.selections.length > 0) {
      // Use the first selection to populate the identifier for backward compatibility
      const sel = parsed.selections[0];
      if (sel.projectId) identifier.projectId = sel.projectId;
      if (sel.familyId) identifier.familyId = sel.familyId;
      if (sel.testLocationId) identifier.testLocationId = sel.testLocationId;
      if (sel.phaseBuilds?.[0]?.phaseId) identifier.phaseId = sel.phaseBuilds[0].phaseId;
      if (sel.phaseBuilds?.[0]?.buildId) identifier.buildId = sel.phaseBuilds[0].buildId;
    } else if (parsed.identifier) {
      identifier = parsed.identifier;
    }

    // Map identifier keys through IdentifierKeyMap for consistency
    const newIdentifier: Partial<Record<TIdentifierValue, string>> = Object.fromEntries(
      Object.entries(identifier)
        .map(([key, value]) => {
          const identifierKey = Object.entries(IdentifierKeyMap).find(entry => entry[1] === key);
          if (identifierKey) {
            return [identifierKey[0], value];
          } else {
            return [key, value]; // pass through unknown keys as-is
          }
        })
        .filter(entry => entry.length > 0),
    );

    return {
      identifier: newIdentifier,
      ...parsed,
      ...meta,
    };
  }
  return null;
}
function createRoute(configList: IConfig[], layer = 1): RouteObject[] {
  return configList.map(config => {
    const { children, redirect, ...rest } = config;

    // Handle redirect routes
    if (redirect) {
      const route: RouteObject = {
        ...rest,
        element: <Navigate to={redirect} replace />,
      };
      return route;
    }

    // Look up component from MODULE_REGISTRY using the key
    const registryEntry = MODULE_REGISTRY[config.key];
    const PageComponent = registryEntry?.component;
    const pageProps = registryEntry?.props;

    const route: RouteObject = {
      ...rest,
      lazy: async () => {
        return {
          ...(PageComponent && pageProps ? { element: <PageComponent {...pageProps} /> } : { Component: PageComponent ?? undefined }),
          loader: !children?.length ? infoLoader : undefined,
        };
      },
      hasErrorBoundary: layer == 1,
      ErrorBoundary: layer == 1 ? () => ErrorBoundaryElement() : undefined,
      element: layer == 1 ? <MainLayout /> : undefined,
    };

    if (children) {
      route.children = createRoute(children, layer + 1);
    }
    return route;
  });
}

function Router() {
  const dispatch = useAppDispatch();
  const routerData = useAppSelector(state => state.router);
  const [router, setRouter] = useState<RouterProviderProps["router"]>();

  useEffect(() => {
    fetch_RouterConfig().then(res => {
      const menus = (res as any).menus ?? res.data ?? [];
      dispatch(updateRouterConfig(menus));
    });
  }, []);

  // Build routes whenever menuConfig is populated from the API
  useEffect(() => {
    if (routerData.menuConfig.length > 0) {
      setRouter(createBrowserRouter(createRoute(routerData.menuConfig)));
    }
  }, [routerData.menuConfig]);

  return <>{!!router && <RouterProvider router={router} />}</>;
}
export default Router;
